import { T } from '@angular/cdk/keycodes';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, Subject } from 'rxjs';
import { map, mergeMap, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AssetClassModel } from 'src/app/core/asset_class';
import { ReportDesignModel } from './report-design.model';
import { ConditionRatingV2Service } from '../services/condition-rating-v2.service';
import { StandardMeasurementUtility } from 'src/app/core/linked-tables/_services/standard-measurement-utility.service';
import { ReportDesignService } from './report-design.service';
import { Location } from '@angular/common';
import { DesignLayoutV2Component } from '../reporting-process/design-layout-v2/design-layout-v2.component';
import { ToeModel } from 'src/app/core/toe';
import { SupportingValuerModel } from 'src/app/core/toe/_models/supporting-valuer.model';
import { SupportingDocumentsV2Component } from './components/supporting-documents.component';
import { AgencyModel, AgencyService, AllAppendicesRequested, AllAppendixCategoriesRequested } from 'src/app/core/admin';
import { AssignmentsService } from 'src/app/core/assignment';
import { Store } from '@ngrx/store';

@Component({
  selector: 'kt-report-design-page',
  templateUrl: './report-design-page.component.html',
  styleUrls: ['./report-design-page.component.scss'],
  providers: [ConditionRatingV2Service, StandardMeasurementUtility]
})
export class ReportDesignPageComponent implements OnInit, OnDestroy {
  @ViewChild(DesignLayoutV2Component)
  designLayoutV2Component: DesignLayoutV2Component;
  @ViewChild(SupportingDocumentsV2Component)
  supportingDocuments: SupportingDocumentsV2Component;

  private _onDestroy: Subject<void> = new Subject();
  tp: AssetClassModel;
  toe: ToeModel;
  leadValuer: SupportingValuerModel;
  members: SupportingValuerModel[] = [];
  agency: AgencyModel;

  reporting: ReportDesignModel

  constructor(
    private activatedRoute: ActivatedRoute,
    private crService: ConditionRatingV2Service,
    private smUtilityService: StandardMeasurementUtility,
    private service: ReportDesignService,
    private location: Location,
    private cdf: ChangeDetectorRef,
    private assignmentService: AssignmentsService,
    private agencyService: AgencyService,
    private store$: Store
  ) { }

  ngOnInit(): void {
    this.store$.dispatch(new AllAppendixCategoriesRequested());
    this.store$.dispatch(new AllAppendicesRequested());
    this.activatedRoute.data
      .pipe(
        takeUntil(this._onDestroy),
        map((data) => {
          this.tp = Object.assign({}, data.assetClassData.data)
          this.toe = Object.assign({}, data.toeData.data) as ToeModel;
          const workers = data.workersData.data;
          this.leadValuer = workers.find(
              (w) => w.lead_valuer == 1
          );
          this.members = workers.filter(
              (w) => w.lead_valuer != 1
          );

          this.crService.setComponentsAll(
            this.smUtilityService,
            this.tp.details,
            this.tp
          )

          this.crService.setConditionRatingsFromRatingModels(
            data.subData.ratings
          )
          this.crService.setSchemeId(
            this.tp.details.aboutProperty.floor_numbering_scheme_id
          )

          return {
            toe: this.toe,
            tp: this.tp
          };
        }),
        mergeMap(({ toe, tp }) => {
            return combineLatest([
                this.assignmentService
                    .getById(toe.assignment_id)
                    .pipe(map((res) => res.data)),
                this.service.get(tp.id)
            ]);
        }),
        mergeMap(([assignment, reporting]) => {
          this.reporting = reporting?.data
          if (this.reporting == undefined || this.reporting == null) {
            this.reporting = this.createInitialReportDesignModel(
              this.toe,
              this.leadValuer,
              this.members
            )
          }
          if (this.reporting.toe_related_docs == null) {
            this.reporting = {
              ...this.reporting,
              toe_related_docs: this._toeRelatedDocs(
                this.toe,
                this.leadValuer,
                this.members
              )
            }
          }
          return this.agencyService.getByAssignmentId(assignment.id).pipe(
            map(res => res.data)
          )
        })
      ).subscribe(agency => {
        this.agency = agency
        this.cdf.markForCheck()
      })

      //   tap(reporting => {
      //     if (reporting) {
      //       this.reporting = reporting.data
      //     } else {
      //       this.reporting = this.createInitialReportDesignModel(
      //         this.toe,
      //         this.leadValuer,
      //         this.members
      //       ) 
      //     }
      //   })
      // ).subscribe()
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  private createInitialReportDesignModel(
    toe: ToeModel,
    leadValuer: SupportingValuerModel,
    members: SupportingValuerModel[]
  ) {
    const data: ReportDesignModel = {
      id: undefined,
      status: 0,
      tp_selected_pictures: [],
      tp_pictures_all_or_custom: 0,
      building_pictures: [],
      comparable_pictures: 0,
      matterport_contents: [],
      property_pictures: [],
      valuation_cover_picture: 0,
      videos: [],
      appendices: [],
      toe_related_docs: this._toeRelatedDocs(toe, leadValuer, members),
      otherDocuments: []
    }
    return data
  }

  onSubmit(complete: boolean) {
    var desingData = this.designLayoutV2Component.designData

    this.service.upsert(
      {
        id: this.reporting.id,
        status: complete ? 2 : 1,
        comparable_pictures: desingData.comparable_pictures,
        valuation_cover_picture: desingData.report_cover,
        tp_pictures_all_or_custom: desingData.picture_location,
        property_pictures: desingData.property_pics.map(
          (pic: { type: any; id: any; selection_status: any }) => {
            return {
              type: pic.type,
              pic_id: pic.id,
              selection: pic.selection_status,
            };
          }
        ),
        building_pictures: desingData.building_pics.map(
          (pic: { type: any; id: any; selection_status: any }) => {
            return {
              type: pic.type,
              pic_id: pic.id,
              selection: pic.selection_status,
            };
          }
        ),
        videos: desingData.videos.map(
          (vid: { id: any; selection_status: any }) => {
            return {
              vid_id: vid.id,
              selection: vid.selection_status,
            };
          }
        ),
        matterport_contents: desingData.links.map(
          (link: { id: any; selection_status: any }) => {
            return {
              link_id: link.id,
              selection: link.selection_status,
            };
          }
        ),
        tp_selected_pictures: [],
        appendices: this.supportingDocuments.getAppendicesFiles(),
        otherDocuments: this.supportingDocuments
            .getUploadFiles()
            .map((f) => {
                return {
                    id: f.id ? f.id : null,
                    title: f.title,
                    descr: f.descr,
                    name: f.name,
                    size: f.size,
                    path: f.path,
                };
            }),
        toe_related_docs: this.supportingDocuments.getToeDocs(),
      },
      this.tp.id
    ).subscribe(_ => {
      this.location.back();
    })
  }

    private _toeRelatedDocs(
        toe: ToeModel,
        leadValuer: SupportingValuerModel,
        members: SupportingValuerModel[]
    ) {
        return {
            agency_documents: toe.selected_agency_documents,
            lead_valuer: {
                certificates: leadValuer ? leadValuer.selected_certificate : [],
                documents: leadValuer ? leadValuer.selected_document : [],
            },
            members: members.map((m) => ({
                id: m.id,
                certificates: m.selected_certificate,
                documents: m.selected_document,
            })),
            otherDocs: toe.additional_files.map((f) => f.id),
        };
    }
}
